/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */

// @flow
import type { Node } from 'react';

import classNames from 'classnames';
import React from 'react';
// eslint-disable-next-line import/extensions
import { FaCheck, FaRegCircle } from 'react-icons/fa';
import { Progress, Spinner } from 'reactstrap';
import type { ExtractionRowState } from '#support/models/extraction/describeExtractionProgress.js';
import describeExtractionProgress from '#support/models/extraction/describeExtractionProgress.js';
import {
  bar as barClass,
  detail as detailClass,
  stage as stageClass,
} from '../ExtractionProgress.module.scss';
import {
  count as countClass,
  stepHeading,
  stepIcon,
  stepIconDone,
  stepIconPending,
  stepList,
  stepMeta,
  stepRow,
  stepRowActive,
  stepRowDone,
  stepSpinner,
  stepSpinnerStill,
} from './ExtractionStages.module.scss';

type Props = {|
  status: string,
  progress: ?{ [string]: mixed },
  // false once polling has failed: nothing on the page may then claim the job is moving.
  live: boolean,
|};

const STAGE_LABEL_ID = 'extractionStageLabel';

// Read by screen readers after each row's heading.
const STATE_WORD = { done: 'done', active: 'in progress', pending: 'waiting' };

const ROW_CLASS = { done: stepRowDone, active: stepRowActive, pending: undefined };

const renderIcon = (state: ExtractionRowState, live: boolean): Node => {
  switch (state) {
    case 'done':
      return <FaCheck className={stepIconDone} />;
    case 'active':
      // Bootstrap's spinner (SubmitButton is the app's other one). reactstrap spreads attributes
      // after its own role="status", so the override takes and the stage line stays the page's
      // only live region; the empty child drops its hidden "Loading..." text.
      return (
        <Spinner
          tag="span"
          size="sm"
          color="primary"
          role="presentation"
          aria-hidden="true"
          className={classNames(stepSpinner, { [stepSpinnerStill]: !live })}
        >
          {null}
        </Spinner>
      );
    default:
      return <FaRegCircle className={stepIconPending} />;
  }
};

/*
 * What SP3-AI-ReportReader is doing right now, as a short job log: the stage line, an honest bar,
 * the section count, and the three steps with one in flight. A pure view of the latest poll
 * (describeExtractionProgress); it keeps no state of its own.
 *
 * The plain `extraction-progress-steps` / `extraction-step-heading` classes and `data-live` are
 * hooks for specs, which must not depend on the hashed module class names.
 */
const ExtractionStages = ({ status, progress, live }: Props): Node => {
  const { percent, stageLabel, detail, rows, isWorking } = describeExtractionProgress({
    status,
    progress,
  });

  return (
    <>
      <p
        id={STAGE_LABEL_ID}
        className={stageClass}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {stageLabel}
      </p>
      <Progress
        value={percent}
        animated={live && isWorking}
        className={barClass}
        barAriaLabelledBy={STAGE_LABEL_ID}
        barAriaValueText={`${percent}% complete`}
      />
      {detail && <p className={classNames(detailClass, countClass)}>{detail}</p>}
      {rows.length > 0 && (
        // list-style: none makes WebKit drop the list role unless it is stated, so the role is not
        // redundant there despite the lint rule.
        // eslint-disable-next-line jsx-a11y/no-redundant-roles
        <ol
          role="list"
          className={`${stepList} extraction-progress-steps`}
          aria-label="Reading steps"
          data-live={live ? 'true' : 'false'}
        >
          {rows.map(({ key, heading, state, meta }) => (
            <li
              key={key}
              className={classNames(stepRow, ROW_CLASS[state])}
              aria-current={state === 'active' ? 'step' : undefined}
            >
              <span className={stepIcon} aria-hidden="true">
                {renderIcon(state, live)}
              </span>
              <span className={`${stepHeading} extraction-step-heading`}>{heading}</span>
              {meta && <span className={stepMeta}>{meta}</span>}
              <span className="sr-only">{STATE_WORD[state]}</span>
            </li>
          ))}
        </ol>
      )}
    </>
  );
};

export default ExtractionStages;
